import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { Gift, TrendingUp, ShoppingBag, ArrowDown, ArrowUp } from 'lucide-react';

interface RewardBalance {
  balance: number;
  lifetime_earned: number;
  pending_orders: number;
  currency: string;
}

interface RewardTransaction {
  id: string;
  amount: number;
  type: string; // earned, redeemed, expired
  description: string;
  reference_id: string | null;
  created_at: string;
}

const Rewards: React.FC = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<RewardBalance | null>(null);
  const [transactions, setTransactions] = useState<RewardTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [balanceRes, txnsRes] = await Promise.all([
        axios.get(API.getRewardBalance(), { headers }),
        axios.get(API.listRewardTransactions(), { headers }),
      ]);
      setBalance(balanceRes.data as RewardBalance);
      setTransactions(txnsRes.data as RewardTransaction[]);
    } catch (err) {
      console.error('Failed to load reward data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <div className="main-content">Loading rewards...</div>;
  if (!user) return <div>Not authenticated</div>;

  return (
    <div className="main-content">
      <div style={{ marginBottom: '32px' }}>
        <h1 className="page-title" style={{ margin: '0 0 8px' }}>
          My Rewards
        </h1>
        <p style={{ color: '#64748B', fontSize: '14px' }}>
          Your cash reward points earned through IncordUK procurement.
        </p>
      </div>

      <div
        style={{
          background: 'linear-gradient(135deg, #1C1C1C 0%, #3D3D3D 100%)',
          borderRadius: '12px',
          padding: '40px',
          color: '#fff',
          marginBottom: '40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '24px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div
              style={{
                padding: '12px',
                background: 'rgba(212, 175, 55, 0.15)',
                borderRadius: '10px',
                color: '#D4AF37',
              }}
            >
              <Gift size={28} />
            </div>
            <div>
              <div style={{ fontSize: '13px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px' }}>
                Available Balance
              </div>
              <div style={{ fontSize: '42px', fontWeight: 700, lineHeight: 1.1 }}>
                £{balance?.balance.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', marginTop: '8px' }}>
            <div>
              <div style={{ fontSize: '11px', opacity: 0.6, textTransform: 'uppercase' }}>Lifetime Earned</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>£{balance?.lifetime_earned.toFixed(2) || '0.00'}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', opacity: 0.6, textTransform: 'uppercase' }}>Currency</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{balance?.currency || 'GBP'}</div>
            </div>
          </div>
        </div>
        <div
          style={{
            textAlign: 'center',
            padding: '16px 32px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#D4AF37' }}>up to 5%</div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>Reward Rate</div>
          <div style={{ fontSize: '11px', opacity: 0.5, marginTop: '4px' }}>on qualifying orders</div>
        </div>
      </div>

      <div
        className="section-card"
        style={{
          padding: '24px',
          marginBottom: '32px',
          display: 'flex',
          gap: '24px',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ padding: '12px', background: '#FEF9C3', borderRadius: '8px', color: '#854D0E' }}>
          <TrendingUp size={24} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '16px' }}>How Rewards Work</h3>
          <p style={{ margin: 0, color: '#64748B', fontSize: '14px', lineHeight: 1.6 }}>
            Standard orders earn <strong>5% of the compliant invoice value, minus a £10 handling fee per
            item</strong> — so larger orders see a bigger reward, while small single-item orders may earn
            £0. Partner Card members earn a flat <strong>8%</strong> instead. Rewards are credited after
            the order is marked as <strong>delivered</strong> and the supplier invoice is verified. Use
            your rewards on future purchases.
          </p>
        </div>
      </div>

      <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--sidebar-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 className="card-title" style={{ margin: 0 }}>
            Transaction History
          </h3>
          <span style={{ fontSize: '13px', color: '#64748B' }}>
            {transactions.length} entries
          </span>
        </div>

        {transactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94A3B8' }}>
            <ShoppingBag size={48} style={{ opacity: 0.15, marginBottom: '16px' }} />
            <p>No reward transactions yet. Start shopping to earn rewards!</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0' }}>
            {transactions.map((txn) => {
              // Derive the sign/colour from the transaction's actual signed
              // amount, not from `type` — "shipping_adjustment" (BDD 4-A
              // line 240-241's true-up) can legitimately be a credit *or* a
              // deduction depending on whether the real carrier invoice came
              // in above or below the estimate, and matching on
              // type === 'earned' always rendered it as a deduction
              // regardless of its real sign, which would show a genuine
              // credit to the customer as if they'd been charged. Found
              // while re-auditing the reconciliation feature (todo.txt 19).
              const isCredit = txn.amount >= 0;
              return (
                <div
                  key={txn.id}
                  style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid var(--sidebar-border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        padding: '8px',
                        borderRadius: '8px',
                        background: isCredit ? '#DCFCE7' : '#FEF2F2',
                        color: isCredit ? '#10B981' : '#EF4444',
                      }}
                    >
                      {isCredit ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{txn.description}</div>
                      <div style={{ fontSize: '12px', color: '#64748B' }}>
                        {new Date(txn.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '16px',
                      color: isCredit ? '#10B981' : '#EF4444',
                    }}
                  >
                    {isCredit ? '+' : '-'}£{Math.abs(txn.amount).toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Rewards;
